import React, { useEffect, useState } from "react";
import Card from "../../theme/untitled/Card/Card";
import Button from "../../theme/untitled/Button/Button";
import Input from "../../theme/untitled/Input/Input";
import Avatar from "../../theme/untitled/Avatar/Avatar";
import Icon from "../../theme/untitled/icons/Icon";
import styles from "./SettingsPage.module.scss";
import { parseEditError } from "./errorHelpers";

const fieldsFromModel = (userModel) => ({
  first_name: userModel.first_name || "",
  last_name: userModel.last_name || "",
  email: userModel.email || "",
});

const BasicInformationCard = ({ userModel, user, editUser }) => {
  const [draft, setDraft] = useState(() => fieldsFromModel(userModel));
  const [justSaved, setJustSaved] = useState(false);

  const baseline = fieldsFromModel(userModel);
  const isDirty =
    draft.first_name !== baseline.first_name ||
    draft.last_name !== baseline.last_name ||
    draft.email !== baseline.email;

  const loading = user.edit;

  useEffect(() => {
    if (user.editSuccess) {
      setDraft(fieldsFromModel(userModel));
      setJustSaved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.editSuccess]);

  const { fieldErrors, generalMessage } = user.editError
    ? parseEditError(user.editError)
    : { fieldErrors: {}, generalMessage: null };
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const onChangeValue = (field) => (e) => {
    setJustSaved(false);
    setDraft((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const onCancel = () => {
    setDraft(baseline);
    setJustSaved(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    editUser(userModel.uuid, draft);
  };

  const isValid = draft.first_name.trim() && draft.email.trim();
  const saveDisabled = loading || !isDirty || !isValid;
  const cancelDisabled = loading || !isDirty;

  return (
    <Card aria-labelledby="basic-information-title">
      <Card.Header
        id="basic-information-title"
        title="Basic information"
        subtitle="Your name and email, as workspace members see them."
      />
      <form onSubmit={onSubmit}>
        <Card.Body>
          <div className={styles.identity}>
            <Avatar
              className={styles.identityAvatar}
              icon={<Icon name="User01" size={32} />}
            />
            <div className={styles.identityName}>
              {baseline.first_name} {baseline.last_name}
            </div>
          </div>

          <div className={styles.grid}>
            <Input
              label="First name"
              value={draft.first_name}
              onChange={onChangeValue("first_name")}
              readOnly={loading}
              maxLength={32}
              error={fieldErrors.first_name}
            />
            <Input
              label="Last name"
              value={draft.last_name}
              onChange={onChangeValue("last_name")}
              readOnly={loading}
              maxLength={64}
              error={fieldErrors.last_name}
            />
            <Input
              label="Email"
              type="email"
              value={draft.email}
              onChange={onChangeValue("email")}
              readOnly={loading}
              hint={
                fieldErrors.email
                  ? undefined
                  : "Used for sign-in and account notices."
              }
              error={fieldErrors.email}
            />
          </div>
        </Card.Body>

        <Card.Footer className={styles.cardFooter}>
          {generalMessage && (
            <span
              className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}
            >
              <Icon
                name="AlertCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
              />
              <span>
                <strong>Couldn't save your changes.</strong> {generalMessage}
              </span>
            </span>
          )}
          {!generalMessage && hasFieldErrors && (
            <span
              className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}
            >
              <Icon
                name="AlertCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
              />
              <span>
                <strong>Changes were not saved.</strong> Fix the field above and
                try again.
              </span>
            </span>
          )}
          {!generalMessage && !hasFieldErrors && justSaved && !isDirty && (
            <span
              className={`${styles.footerMessage} ${styles["footerMessage--success"]}`}
            >
              <Icon
                name="CheckCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--success"]}`}
              />
              <span>Saved just now</span>
            </span>
          )}

          <div className={styles.cardFooterActions}>
            <Button
              hierarchy="secondaryGray"
              disabled={cancelDisabled}
              onClick={onCancel}
              type="button"
            >
              Restore
            </Button>
            <Button
              hierarchy="primary"
              type="submit"
              disabled={saveDisabled}
              loading={loading}
            >
              {loading
                ? "Saving…"
                : generalMessage
                ? "Try again"
                : "Save changes"}
            </Button>
          </div>
        </Card.Footer>
      </form>
    </Card>
  );
};

export default BasicInformationCard;
